<!--
  StickySlot
  ---
  A generic component for sticky content. Works by generating a new, absolute position 'dock' element
  as a sibling to the sticky 'container' element of the content, and measuring their intersection.
-->

<template>
  <div class="sticky-slot-container">
    <slot />
  </div>
</template>

<script>
let id_index = 0;

export default {
  props: {
    // custom callback when intersection is complete
    onDocked: {
      type: Function,
      required: false,
      // eslint-disable-next-line no-empty-function
      default: () => (() => {}),
    },
    // indicates how much of the _dock_ (position: absolute) element needs to
    // intersect with the viewport before `callback` is triggered
    threshold: {
      type: Number,
      default: 1,
    },
  },
  data: () => ({
    self_id: null,
    dock_el: null,
    container_el: null,
  }),
  beforeMount() {
    this.self_id = id_index++;
  },
  mounted() {
    // need to create the controls dock, insert it into position
    const dock_el = document.createElement('div');
    dock_el.id = `sticky-slot-dock-${this.self_id}`;
    dock_el.classList.add('sticky-slot-dock');
    const container_el = document.querySelector('.sticky-slot-container');
    // the dock should be as close to the same size as the controls el as possible for an accurate insersection
    dock_el.style.height = `${container_el.clientHeight}px`;
    dock_el.style.width = `${container_el.clientWidth}px`;
    dock_el.style.position = 'absolute';
    container_el.parentElement.insertBefore(dock_el, container_el);

    // record these elements
    this.dock_el = dock_el;
    this.container_el = container_el;

    // css has no psuedo-class for sticky elements which have rested in their 'relative' position
    // so we need to do it in the script manually
    // to clarify: this code checks the intersection between #sticky-slot-dock and the browser's viewport;
    // when the element is 100% intersecting, the callback is triggered and we use isIntersecting

    // set up the observer (https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
    // here we invoke the custom callback and pass the controller elements as well as the intersection event
    const obs = new IntersectionObserver(
      ([ev]) => this.onDocked({
        elements: {
          dock_el: this.dock_el,
          container_el: this.container_el,
        },
        event: ev,
      }),
      {
        threshold: [this.threshold],
      },
    );
    obs.observe(dock_el); // begin observing the #sticky-slot-dock
  },
};
</script>

<style lang="scss" scoped>
.sticky-slot-container {
  position: sticky;
  bottom: 0;
  margin-bottom: -1px;
  z-index: 20;
}
</style>
